import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getSoftLandingPath } from '../utils/rbacUtils';
import { userDashboardLinks } from '../routes/navConfig.jsx';
import usePermission from '../hooks/usePermission.js';
import { routeAccessDecision } from '../utils/permissionLevel.js';
import { useHeldRefusal } from './accessHold.js';

/**
 * Route-level access protection — page-level permission from effectivePermissions, plus an optional
 * hard super-user gate. (There is no role-hierarchy check: no route gates on org/game-owner roles;
 * access is decided by RBAC page permissions. See docs/access-role-derivation-migration.md.)
 *
 * Props:
 *   pagePath      - RBAC page id (e.g. PAGES.PAGE_MGMT) — blocks if NO_ACCESS
 *   anyOfPagePath - alternate RBAC page path; access is allowed if EITHER pagePath OR this one is
 *                   accessible. Used by surfaces reachable from two audiences — e.g. the shared
 *                   Grant Access wizard, opened from both org-user (org admins) and game-user
 *                   (game owners) management. The backend still authorizes the actual operation.
 *   superUserOnly - hard super-user gate (e.g. Developer Tools) — NOT overridable via RBAC
 *   componentPath - optional RBAC component that must ALSO resolve to at least `minLevel`. For routes that
 *                   only make sense with a write grant (a "create" or "edit" page): reading the page is not
 *                   enough to open them.
 *   minLevel      - the level `componentPath` needs (default READ_ONLY)
 */
export const RoleProtectedRoute = ({
    pagePath, anyOfPagePath, superUserOnly = false, componentPath, minLevel = 'READ_ONLY', children,
}) => {
    const userRole = useSelector(state => state.dashboardContext.userRole);
    const isSuperUser = useSelector(state => state.userDetails.superUser);
    const userDetailsLoaded = useSelector(state => state.userDetails.userDetailsLoaded);
    const effectivePermissions = useSelector(state => state.effectivePermissions);
    const location = useLocation();
    const pageLevel = usePermission(pagePath || '');
    const altPageLevel = usePermission(anyOfPagePath || '');
    const componentLevel = usePermission(componentPath || '');

    // A soft RBAC denial (the user lacks THIS page/role but may have others) shouldn't dead-end on
    // /not-authorized. This happens routinely when switching org/game context: a page that was
    // accessible in the old org resolves to NO_ACCESS in the new one. Prefer landing them on Home (the
    // friendly neutral landing) when they can reach it, else the first page they CAN open. Either way
    // the result is an accessible path (or /not-authorized when they have access to nothing), so no
    // redirect loop, and we never bounce back to the page we just left. Hard gates (superUserOnly,
    // unauthenticated, org-suspension in ProtectedRoute) keep the explicit /not-authorized dead-end.
    const softDenied = () => {
        const fallback = getSoftLandingPath(userDashboardLinks, userRole, effectivePermissions);
        const target = (fallback && fallback !== location.pathname) ? fallback : '/not-authorized';
        return <Navigate to={target} replace />;
    };

    // The decision itself is a pure rule (utils/permissionLevel.js, unit-tested); this component only renders it.
    const decision = routeAccessDecision({
        superUserOnly, isSuperUser, userDetailsLoaded,
        pagePath, pageLevel, anyOfPagePath, altPageLevel,
        componentPath, componentLevel, minLevel,
    });
    // Refused while this page was open (access re-read in place): held - reported for the banner, page kept.
    const keepPage = useHeldRefusal(location.pathname, decision === 'soft-deny' || decision === 'hard-deny',
        'You no longer have access to this page.');
    if (decision === 'wait') return null;
    if (keepPage) return children;
    if (decision === 'hard-deny') return <Navigate to="/not-authorized" replace />;
    if (decision === 'soft-deny') return softDenied();
    return children;
};
